import { html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { when } from "lit/directives/when.js";

import DeviceController from "@/controllers/device-controller";
import { formatDate } from "@/internals/date-utils";
import { emit } from "@/internals/events";

import type { NotificationCardStatus } from "./types";
import type AtlasCollapsibleText from "@/components/display/atlas-collapsible-text/atlas-collapsible-text";
import AtlasElement, { type AtlasElementProps } from "@/components/atlas-element";
import styles from "./atlas-notification-center-card.scss";

import "@/components/display/atlas-collapse-button/atlas-collapse-button";
import "@/components/display/atlas-collapsible-text/atlas-collapsible-text";
import "@/components/display/atlas-icon/atlas-icon";
import "@/components/display/atlas-text/atlas-text";

export type NotificationCenterCardProps = AtlasElementProps & {
    "header": string;
    "date": string;
    "status": NotificationCardStatus;
    "icon": string;
    "read": boolean;
    "disable-ellipsis": boolean;
    "href": string;
    "has-action": boolean;
};

/**
 * @dependency atlas-collapsible-text
 * @dependency atlas-collapse-button
 * @dependency atlas-icon
 * @dependency atlas-text
 *
 * @event {CustomEvent} atlas-notification-center-card-click - Evento emitido ao clicar na notificação
 *
 * @slot default - Slot para o conteúdo da notificação
 *
 * @tag atlas-notification-center-card
 */
@customElement("atlas-notification-center-card")
export default class AtlasNotificationCenterCard extends AtlasElement {
    static styles = styles;

    /** Título da notificação */
    @property({ type: String }) header: string;

    /** Data que ocorreu a notificação */
    @property({ type: String }) date: string;

    /** Situação da notificação */
    @property({ type: String }) status: NotificationCardStatus = "none";

    /** Ícone da notificação */
    @property({ type: String }) icon: string;

    /** Indica se a notificação já foi lida */
    @property({ type: Boolean }) read: boolean = false;

    /** Indica se o título e texto da notificação não devem ser cortados com reticências */
    @property({ type: Boolean, attribute: "disable-ellipsis" }) disableEllipsis: boolean = false;

    /** URL para onde o usuário será redirecionado ao clicar na notificação */
    @property({ type: String }) href: string;

    /** Indica se a notificação tem ação de clique, para que o evento seja emitido ao clicar nela */
    @property({ type: Boolean, attribute: "has-action" }) hasAction: boolean = false;

    @state() private _showCollapseButton = true;

    @state() private _collapsed = true;

    private _deviceController = new DeviceController(this);

    constructor() {
        super();

        this.verifyCanShowCollapseButton = this.verifyCanShowCollapseButton.bind(this);
        this.onClickToggleCollapse = this.onClickToggleCollapse.bind(this);
    }

    /** @internal */
    public connectedCallback(): void {
        super.connectedCallback();

        this.verifyCanShowCollapseButton();
    }

    private getStatusIcon() {
        const statusIconMap = {
            "success": "check",
            "error": "x",
            "attention": "alert",
            "in progress": "clock",
            "none": ""
        };

        return statusIconMap[this.status] || "";
    }

    private getStatusTheme() {
        const statusThemeMap = {
            "success": "success",
            "error": "danger",
            "attention": "warning",
            "in progress": "info",
            "none": ""
        };

        return statusThemeMap[this.status] || "";
    }

    private textElementHasEllipsis(textElement: AtlasCollapsibleText) {
        const wrapper = textElement.getTextWrapper();
        const contentHeight = wrapper.getBoundingClientRect().height;

        return Math.round(contentHeight) < wrapper.scrollHeight;
    }

    private async verifyCanShowCollapseButton() {
        await this.updateComplete;

        const titleHasEllipsis = this.textElementHasEllipsis(this.shadowRoot.querySelector(".notification-title"));
        const contentHasEllipsis = this.textElementHasEllipsis(this.shadowRoot.querySelector(".notification-text"));

        this._showCollapseButton = titleHasEllipsis || contentHasEllipsis;
    }

    private onClickToggleCollapse(event: PointerEvent) {
        event.stopPropagation();
        event.preventDefault();

        this._collapsed = !this._collapsed;

        this.shadowRoot.querySelectorAll("atlas-collapsible-text").forEach((text) => {
            text.toggleExpandText();
        });
    }

    private onClickNotification() {
        emit(this, "atlas-notification-center-card-click", {});
    }

    private renderStatusIcon() {
        return when(
            this.status !== "none" && !this.skeletonLoading,
            () => html`
                <div class="visual-support-status bg-${this.getStatusTheme()}">
                    <atlas-icon name="${this.getStatusIcon()}" size="auto" white></atlas-icon>
                </div>
            `
        );
    }

    private renderCollapseButton() {
        return when(
            !this.disableEllipsis && this._showCollapseButton,
            () => html`
                <atlas-collapse-button
                    size="2x"
                    ?collapsed=${this._collapsed}
                    @click=${this.onClickToggleCollapse}
                    class="collapse-button"
                ></atlas-collapse-button>
            `
        );
    }

    private renderNotificationDate() {
        return when(
            !!this.date,
            () => html`
                <div class="notification-date">
                    <atlas-text size="xs" muted>${formatDate(this.date, { yearLastDigits: true })}</atlas-text>
                </div>
            `
        );
    }

    private renderNotificationContent() {
        return html`
            <div class="visual-support">
                <atlas-icon name="${this.icon}" size="2x"></atlas-icon>
                ${this.renderStatusIcon()}
            </div>
            <div class="body-wrapper">
                <div class="notification-card-header">
                    <atlas-collapsible-text
                        class="notification-title"
                        size=${this._deviceController.isMobile ? "xs" : "sm"}
                        ?bold=${!this.read}
                        ?muted=${this.read}
                        line-clamp=${ifDefined(this.disableEllipsis ? undefined : "1")}
                        hide-toggle-button
                        normal-white-space
                    >
                        ${this.header}
                    </atlas-collapsible-text>
                    ${this.renderNotificationDate()} ${this.renderCollapseButton()}
                </div>
                <atlas-collapsible-text
                    class="notification-text"
                    size="xs"
                    ?muted=${this.read}
                    line-clamp=${ifDefined(this.disableEllipsis ? undefined : "2")}
                    hide-toggle-button
                    normal-white-space
                >
                    <slot @slotchange=${this.verifyCanShowCollapseButton}></slot>
                </atlas-collapsible-text>
            </div>
        `;
    }

    /** @internal */
    public render() {
        if (this.href) {
            return html`
                <a class="notification-card interactive" href="${this.href}" @click=${this.onClickNotification}>
                    ${this.renderNotificationContent()}
                </a>
            `;
        }

        if (this.hasAction) {
            return html`
                <button class="notification-card interactive" @click=${this.onClickNotification}>
                    ${this.renderNotificationContent()}
                </button>
            `;
        }

        return html`<div class="notification-card">${this.renderNotificationContent()}</div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "atlas-notification-center-card": AtlasNotificationCenterCard;
    }
}
